import React, { useCallback } from 'react'
import { Routes, Route, useLocation, Navigate } from 'react-router-dom'
import { HomePage } from './pages/HomePage'
import { HowItWorksPage } from './pages/HowItWorksPage'
import { PricingPage } from './pages/PricingPage'
import { FAQPage } from './pages/FAQPage'
import { GetStartedPage } from './pages/GetStartedPage'
import { ProfileFormPage } from './pages/ProfileFormPage'
import { SpotlightPage } from './pages/SpotlightPage'
import { DirectoryPage } from './pages/DirectoryPage'
import { SpotlightLearnMorePage } from './pages/OverviewPage'
import { ForPractitionersPage } from './pages/ForPractitionersPage'
import { SpotlightHowItWorksPage } from './pages/SpotlightHowItWorksPage'
import { SpotlightPageMockup as SamplesPage } from './pages/SamplesPage'
import { ForProductsPage } from './pages/ForProductsPage'
import { BasicSetupPage } from './pages/onboarding/BasicSetupPage'
import { PreviewPage } from './pages/onboarding/PreviewPage'
import { LandingPageBuilderPage } from './pages/onboarding/LandingPageBuilderPage'
import { DetailedInterviewPage } from './pages/onboarding/DetailedInterviewPage'
import { AssetsPage } from './pages/onboarding/AssetsPage'
import { TargetingPage } from './pages/onboarding/TargetingPage'
import { PackageSelectionPage } from './pages/onboarding/PackageSelectionPage'
import { ConfirmationPage } from './pages/onboarding/ConfirmationPage'
import { DashboardPage } from './pages/DashboardPage'
import { useGoogleOneTap } from './hooks/useGoogleOneTap'
import { OnboardingProvider } from './contexts/OnboardingContext'
import { OnboardingRouteGuard } from './components/onboarding/OnboardingRouteGuard'
import { AdminRouteGuard } from './pages/admin/AdminRouteGuard'
import { AdminLayout } from './pages/admin/AdminLayout'
import { OverviewPage } from './pages/admin/OverviewPage'
import { CampaignsPage } from './pages/admin/CampaignsPage'
import { CampaignDetailPage } from './pages/admin/CampaignDetailPage'
import { PractitionersPage } from './pages/admin/PractitionersPage'
import { PractitionerDetailPage } from './pages/admin/PractitionerDetailPage'
import { TransactionsPage } from './pages/admin/TransactionsPage'

function AppContent() {
  const location = useLocation()
  
  // Only show One Tap on public/marketing pages, not on authenticated pages
  const showOneTap = !location.pathname.startsWith('/spotlight/') && 
                     !location.pathname.startsWith('/for-services/onboarding') &&
                     !location.pathname.startsWith('/for-products/onboarding') &&
                     !location.pathname.startsWith('/dashboard') &&
                     !location.pathname.startsWith('/admin') &&
                     location.pathname !== '/directory' &&
                     location.pathname !== '/search'

  // Handle One Tap success
  const onSuccess = useCallback(async (email: string, idToken: string) => {
    // Store auth token for later use
    localStorage.setItem('firebase_id_token', idToken)
    localStorage.setItem('user_email', email)

    // If user is on a landing page and clicks One Tap, redirect to onboarding
    const isLandingPage = [
      '/',
      '/for-practitioners',
      '/for-services',
      '/for-products',
      '/for-services/get-started',
      '/for-products/get-started',
      '/how-it-works',
      '/pricing',
      '/get-started'
    ].includes(location.pathname)

    if (isLandingPage) {
      // Redirect to the appropriate get-started page based on audience
      if (location.pathname === '/for-products' || location.pathname === '/for-products/get-started') {
        window.location.href = '/for-products/get-started'
      } else if (
        location.pathname === '/for-services' ||
        location.pathname === '/for-services/get-started' ||
        location.pathname === '/for-practitioners'
      ) {
        window.location.href = '/for-services/get-started'
      } else {
        window.location.href = '/get-started'
      }
    } else if (location.pathname === '/profile-form') {
      // The ProfileFormPage will handle this via its own auth flow
      window.dispatchEvent(new CustomEvent('googleOneTapSuccess', {
        detail: { email, idToken }
      }))
    }
    // Otherwise, user is already authenticated and can continue
  }, [location.pathname])

  const onError = useCallback((error: string) => {
    console.error('Google One Tap error:', error)
  }, [])

  useGoogleOneTap({
    onSuccess,
    onError,
    disabled: !showOneTap
  })

  return (
    <OnboardingProvider>
      <Routes>
        {/* Main marketing pages */}
        <Route path="/" element={<SpotlightLearnMorePage />} />
        <Route path="/how-it-works" element={<SpotlightHowItWorksPage />} />
        <Route path="/for-services" element={<ForPractitionersPage />} />
        <Route path="/for-products" element={<ForProductsPage />} />
        {/* Backwards compatible route */}
        <Route path="/for-practitioners" element={<ForPractitionersPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/samples" element={<SamplesPage />} />
        <Route path="/spotlight-mockup" element={<SamplesPage />} />
        
        {/* Legacy routes (from frontend-spotlight) */}
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/get-started" element={<GetStartedPage />} />
        <Route path="/for-services/get-started" element={<GetStartedPage defaultMode="service" />} />
        <Route path="/for-products/get-started" element={<GetStartedPage defaultMode="product" />} />
        <Route path="/profile-form" element={<ProfileFormPage />} />
        <Route path="/directory" element={<DirectoryPage />} />
        <Route path="/search" element={<DirectoryPage />} />
        
        {/* Onboarding routes */}
        <Route
          path="/for-services/onboarding/basic-setup"
          element={
            <OnboardingRouteGuard requiredStep={0} defaultMode="service">
              <BasicSetupPage />
            </OnboardingRouteGuard>
          }
        />
        <Route
          path="/for-services/onboarding/preview"
          element={
            <OnboardingRouteGuard requiredStep={1} defaultMode="service">
              <PreviewPage />
            </OnboardingRouteGuard>
          }
        />
        <Route
          path="/for-services/onboarding/landing-page"
          element={
            <OnboardingRouteGuard requiredStep={2} defaultMode="service">
              <LandingPageBuilderPage />
            </OnboardingRouteGuard>
          }
        />
        <Route
          path="/for-services/onboarding/targeting"
          element={
            <OnboardingRouteGuard requiredStep={3} defaultMode="service">
              <TargetingPage />
            </OnboardingRouteGuard>
          }
        />
        <Route
          path="/for-services/onboarding/package-selection"
          element={
            <OnboardingRouteGuard requiredStep={4} defaultMode="service">
              <PackageSelectionPage />
            </OnboardingRouteGuard>
          }
        />
        <Route
          path="/for-services/onboarding/confirmation"
          element={
            <OnboardingRouteGuard requiredStep={4} defaultMode="service">
              <ConfirmationPage />
            </OnboardingRouteGuard>
          }
        />
        <Route
          path="/for-services/onboarding/details"
          element={
            <OnboardingRouteGuard requiredStep={2} defaultMode="service">
              <DetailedInterviewPage />
            </OnboardingRouteGuard>
          }
        />
        <Route
          path="/for-products/onboarding/basic-setup"
          element={
            <OnboardingRouteGuard requiredStep={0} defaultMode="product">
              <BasicSetupPage />
            </OnboardingRouteGuard>
          }
        />
        <Route
          path="/for-products/onboarding/preview"
          element={
            <OnboardingRouteGuard requiredStep={1} defaultMode="product">
              <PreviewPage />
            </OnboardingRouteGuard>
          }
        />
        <Route
          path="/for-products/onboarding/landing-page"
          element={
            <OnboardingRouteGuard requiredStep={2} defaultMode="product">
              <LandingPageBuilderPage />
            </OnboardingRouteGuard>
          }
        />
        <Route
          path="/for-products/onboarding/targeting"
          element={
            <OnboardingRouteGuard requiredStep={3} defaultMode="product">
              <TargetingPage />
            </OnboardingRouteGuard>
          }
        />
        <Route
          path="/for-products/onboarding/package-selection"
          element={
            <OnboardingRouteGuard requiredStep={4} defaultMode="product">
              <PackageSelectionPage />
            </OnboardingRouteGuard>
          }
        />
        <Route
          path="/for-products/onboarding/confirmation"
          element={
            <OnboardingRouteGuard requiredStep={4} defaultMode="product">
              <ConfirmationPage />
            </OnboardingRouteGuard>
          }
        />
        <Route
          path="/for-products/onboarding/details"
          element={
            <OnboardingRouteGuard requiredStep={2} defaultMode="product">
              <DetailedInterviewPage />
            </OnboardingRouteGuard>
          }
        />
        
        {/* Dashboard */}
        <Route
          path="/dashboard"
          element={
            <OnboardingRouteGuard>
              <DashboardPage />
            </OnboardingRouteGuard>
          }
        />
        
        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <AdminRouteGuard>
              <AdminLayout />
            </AdminRouteGuard>
          }
        >
          <Route index element={<Navigate to="/admin/overview" replace />} />
          <Route path="overview" element={<OverviewPage />} />
          <Route path="campaigns" element={<CampaignsPage />} />
          <Route path="campaigns/:id" element={<CampaignDetailPage />} />
          <Route path="practitioners" element={<PractitionersPage />} />
          <Route path="practitioners/:id" element={<PractitionerDetailPage />} />
          <Route path="transactions" element={<TransactionsPage />} />
        </Route>
        
        {/* Spotlight landing pages (Spotlight-managed) */}
        <Route path="/spotlight/:slug" element={<SpotlightPage />} />

        <Route path="/:slug" element={<SpotlightPage />} />
      </Routes>
    </OnboardingProvider>
  )
}

function App() {
  return <AppContent />
}

export default App
